import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import ReactPaginate from 'react-paginate';
import queryString from 'query-string';
import 'intersection-observer';

import { Brandfolder, processCustomFields, refreshSection } from '@helpers/show_page_helpers';
import { SearchOperatorOptions } from '@components/show_page/sections/section_search/SearchTypes';
import Checkbox from '@components/common/checkbox/main';
import oldType from '@helpers/oldType';
import Tooltip from '@components/common/tooltip/main';
import UploadArea from '@components/common/filestack_uploader/upload_area';
import AssetUppy from '@components/common/uppy/asset_uppy';
import CanvaButton from '@components/canva/main';

import syncInDesign from '@api/v3/printui';
import moveAsset from '@api/sections/move-asset';
import getResourceAssets from '@api/v4/resources/assets';

import Asset from './asset/asset';
import SectionShroud from './sectionShroud';
import SectionsContext from './sectionsContext';

import './styles/section.scss';

class Section extends React.Component {
  assetRefs = {}

  constructor(props) {
    super(props);

    this.state = {
      isSyncingInDesign: false,
      insertCaret: null,
      isRendered: props.isRendered,
    };
  }

  componentDidMount() {
    this.addReloadListener();
  }

  componentWillUnmount() {
    this.removeReloadListener();
  }

  toggleSelected = (assetKeys, selectAllPages) => {
    this.props.toggleSelected(assetKeys, {
      selectAllPages,
      sectionKey: this.props.section.section_key,
    });
  }

  toggleSelectedAssets = async (type, { activeLabelKey }) => {
    const {
      activeSection,
      allAssetsSelected,
      section,
      toggleSelected,
      userViewOptions,
    } = this.props;

    const selectCurrentPage = type === 'page' && (allAssetsSelected || !this.sectionSelected());
    const entireSection = type === 'all';

    const getAllAssetKeys = async () => {
      const { searchQuery } = this.props;
      const { searchOperator, showSections } = userViewOptions;

      const params = {
        ...searchQuery && { search: searchQuery },
        ...searchQuery && { search_operator: (searchOperator === SearchOperatorOptions.Strict || searchOperator === SearchOperatorOptions.Quoted)
          ? SearchOperatorOptions.OR
          : searchOperator
        },
        digest: BFG.manifestDigest,
        download_request_key: BFG.downloadRequestKey,
        ...activeLabelKey && { labelKeys: [activeLabelKey] },
        onlyKeys: true,
        strict_search: searchOperator === SearchOperatorOptions.Strict,
      };

      const options = {
        activeSection,
        isCollection: BFG.resource.type === 'collection',
        resourceKey: BFG.resource.key,
        resourceType: BFG.resource.type,
        sectionKey: section.section_key,
        showSections,
      };

      const { assets } = await getResourceAssets(params, options);

      return assets;
    };

    if (allAssetsSelected && selectCurrentPage) {
      // we're going from all pages to just the first;
      // for this to work we need to toggle off everything,
      // then proceed with the normal "toggle the first page" dance
      toggleSelected(await getAllAssetKeys(), {});
    }

    let assets = [];
    if (entireSection) {
      assets = await getAllAssetKeys();
    } else {
      assets = this.assetIds();
    }

    const selectAllPages = entireSection && !allAssetsSelected;

    toggleSelected(assets, {
      selectCurrentPage,
      selectAllPages,
      sectionKey: section.section_key,
    });
  };

  goToPage = (data) => {
    this.props.onUpdate({ page: data.selected + 1 });
  }

  syncInDesign = () => {
    this.setState({ isSyncingInDesign: true });
    const { id } = this.props.section;
    syncInDesign(id).then(() => {
      this.props.onUpdate();
      Notify.create({ title: 'Sync complete!', type: 'success' });
    }).catch(() => {
      Notify.create({ title: 'Error during sync. Please try again!', type: 'error' });
    }).finally(() => {
      this.setState({ isSyncingInDesign: false });
    });
  }

  onAssetHover = (item, hoverId, pointerOffset, listView) => {
    if (item.id === hoverId) {
      // nuh uh uh, you can't drag and drop onto yourself. nice try tho
      if (this.state.insertCaret || this.state.insertIndex) {
        this.setState({
          insertCaret: null,
          insertIndex: null,
        });
      }
      return undefined;
    }

    // Additionally, keep users from thinking they can drag assets between sections.
    // Maybe one day we will add this, but today is not that day.
    if (item.sectionId !== this.props.section.section_key) {
      return undefined;
    }

    const draggedAsset = this.props.assets.find((asset) => asset.id === item.id);
    const hoveredAsset = this.props.assets.find((asset) => asset.id === hoverId);

    const dragIndex = draggedAsset ? draggedAsset.attributes.position : -1;
    const hoverIndex = hoveredAsset ? hoveredAsset.attributes.position : -1;

    const hoverNode = findDOMNode(this.assetRefs[hoverId]); // eslint-disable-line react/no-find-dom-node
    const {
      x: hoverX,
      y: hoverY,
      width: hoverWidth,
      height: hoverHeight,
    } = hoverNode.getBoundingClientRect();

    const middleX = hoverX + (hoverWidth / 2);
    const middleY = hoverY + (hoverHeight / 2);
    let direction;
    let insertIndex;
    if (listView) {
      if (pointerOffset.y < middleY) {
        // Insert before element
        direction = 'top';
        insertIndex = hoverIndex;
      } else {
        // Insert after element
        direction = 'bottom';
        insertIndex = hoverIndex + 1;
      }
    } else if (pointerOffset.x < middleX) {
      // Insert before element
      direction = 'left';
      insertIndex = hoverIndex;
    } else {
      // Insert after element
      direction = 'right';
      insertIndex = hoverIndex + 1;
    }

    const adjustedIndex = insertIndex > dragIndex ? insertIndex - 1 : insertIndex;

    if (dragIndex === insertIndex || adjustedIndex === dragIndex) {
      return undefined;
    }

    const existingCaret = this.state.insertCaret;
    // Let's be judicious about our calls to setState...only call it if things have changed
    if (!existingCaret || (existingCaret && (direction !== existingCaret.direction || hoverId !== existingCaret.id))) {
      this.setState({
        insertIndex: adjustedIndex,
        insertCaret: {
          id: hoverId,
          direction,
        },
      });
    }

    return undefined;
  }

  onDrop = (item) => {
    const {
      section,
      authenticityToken,
    } = this.props;
    const { insertIndex } = this.state;

    if (insertIndex === null) { return undefined; }

    const sectionKey = section.section_key;

    moveAsset({
      sectionKey,
      type: section.default_asset_type,
      assetId: item.id,
      index: insertIndex,
      token: authenticityToken,
    }).then(() => {
      refreshSection(sectionKey);
    }).catch((response) => {
      Notify.create(response);
    });

    return undefined;
  }

  onDragComplete = () => {
    this.setState({
      insertIndex: null,
      insertCaret: null,
    });
  }

  isNotActiveSection() {
    const { section, activeSection, userViewOptions } = this.props;
    if (activeSection === "all" || !userViewOptions?.showSections) {
      return false;
    }

    return activeSection && activeSection !== section.section_key;
  }

  removeReloadListener() {
    const { section, onUpdate } = this.props;
    window.removeEventListener(
      `refreshSection${section.section_key}`,
      onUpdate,
      false
    );
  }

  previousLabel() {
    const { currentPage } = this.props;
    return (
      <div
        className={`page-arrow previous ${currentPage === 1 ? "disabled" : ""}`}
      >
        <span className="bff-arrow-left" aria-hidden="true" />
        <p className="pagination-label">{t('Previous')}</p>
      </div>
    );
  }

  nextLabel() {
    const { currentPage, totalPages } = this.props;
    return (
      <div
        className={`page-arrow next ${
          currentPage === totalPages ? "disabled" : ""
        }`}
      >
        <p className="pagination-label">{t('Next')}</p>
        <span className="bff-arrow-right" aria-hidden="true" />
      </div>
    );
  }

  addReloadListener() {
    const { section, onUpdate } = this.props;
    window.addEventListener(
      `refreshSection${section.section_key}`,
      onUpdate,
      false
    );
  }

  sectionSelected() {
    const { selectedAssetKeys, assets } = this.props;

    if (assets.length === 0 || selectedAssetKeys.size < assets.length) {
      // optimization: the whole section can't be selected if there's a size mismatch here
      return false;
    }

    return assets.reduce((a, asset) => a && selectedAssetKeys.has(asset.id), true);
  }

  assetIds() {
    return this.props.assets.map((asset) => asset.id);
  }

  renderUploadForm() {
    const { section } = this.props;
    Notify.makeAll("dormant");
    const type = oldType(section.default_asset_type);
    const url = `/${Brandfolder.slug}/sections/${
      section.section_key
    }/ui/${type}/new`;
    BF.dialog.render(
      `New ${BF.fx.friendlyName(BF.fx.singularize(type))}`,
      url,
      BF.handlerGroups.newAsset
    );
  }

  renderSyncInDesign() {
    const { section, editable, printuiSectionId } = this.props;
    const { isSyncingInDesign } = this.state;

    if (editable && (printuiSectionId === section.id)) {
      return (
        <Tooltip
          tooltipId="indesign-sync-tooltip"
          className="sync-tooltip-component"
          tooltipContent={isSyncingInDesign ? t('Syncing with InDesign') : t('Sync templates with InDesign')}
          offset={"{'right': 8}"}
        >
          <button
            type="button"
            onClick={this.syncInDesign}
            className="sync-indesign-button"
          >
            <span className={`bff-refresh icon ${isSyncingInDesign ? 'syncing' : ''}`} />
          </button>
        </Tooltip>
      );
    }

    return null;
  }

  renderPageCount() {
    const { assets, currentPage, totalAssets, totalPages } = this.props;
    const countPerPage = assets.length;
    let countToPrint = totalAssets;
    if (totalAssets > countPerPage) {
      let start = 1;
      let end = countPerPage;
      if (currentPage !== 1) {
        if (currentPage === totalPages) {
          start = totalAssets - assets.length + 1;
          end = totalAssets;
        } else {
          start = (currentPage - 1) * countPerPage + 1;
          end = start + assets.length - 1;
        }
      }
      countToPrint = `${start} - ${end} ${t('of')} ${totalAssets}`;
    }

    return (
      <p className="asset-count">
        {countToPrint} {assets.length !== 1 ? t('Assets') : t('Asset')}
      </p>
    );
  }

  renderLoadingCards() {
    const { assets } = this.props;
    const screenWidth = window.innerWidth;
    let placeholderCards;
    if (assets.length) {
      placeholderCards = assets.length + 1;
    } else {
      placeholderCards = Math.trunc(screenWidth / 340);
    }
    const cards = [];

    for (let i = 0; i < placeholderCards; i += 1) {
      cards.push(
        <div key={`card-placeholder=${i}`} className="card-placeholder" />
      );
    }

    return (
      <div className="card-list-loading">
        {cards}
      </div>
    );
  }

  renderAssets(activeLabelKey) {
    if (this.props.loading) {
      return (
        <>{this.renderLoadingCards()}</>
      );
    }

    const { insertCaret } = this.state;
    const {
      activeSection,
      assets,
      enableContactSheets,
      lazyLoadCards,
      onAssetProcessing,
      onNewAssets,
      section,
      selectedAssetKeys,
      shiftSelect,
      userViewOptions,
    } = this.props;
    const defaultAssetType = section.default_asset_type;
    const canvaEnabled = BFG.hasFeature("canva_button");

    let newAssetUrl;
    let newAssetUI;
    let newAssetTitle;
    let uploader;
    newAssetTitle = section.default_asset_type;

    if (section.default_asset_type === "ExternalMedium") {
      newAssetTitle = "External Medium";
    }

    if (defaultAssetType !== "GenericFile") {
      newAssetUrl = `/${Brandfolder.slug}/sections/${section.section_key}/ui/${oldType(section.default_asset_type)}/new`;

      const urlParams = {
        ...BF.fx.collectionSlug() && { collection_slug: BF.fx.collectionSlug() },
        ...activeLabelKey && { label_key: activeLabelKey },
      };

      const query = queryString.stringify(urlParams);
      newAssetUrl = query ? `${newAssetUrl}?${query}` : newAssetUrl;

      newAssetUI = () => {
        BF.dialog.render(
          `New ${newAssetTitle}`,
          newAssetUrl,
          BF.handlerGroups.newAsset
        );
      };
    }

    if (BFG.hasFeature('uppy_uploader_features') && defaultAssetType === "GenericFile") {
      uploader = (
        <div className="base-card uppy">
          <AssetUppy
            activeLabelKey={activeLabelKey}
            newResources={onNewAssets}
            onAssetProcessing={onAssetProcessing}
            sectionKey={section.section_key}
          />
        </div>
      );
    } else if (defaultAssetType === "GenericFile") {
      uploader = (
        <div className={`base-card filestack-container set-min-height ${canvaEnabled === true ? "canva-filestack-container" : ""}`}>
          <UploadArea
            activeLabelKey={activeLabelKey}
            hideIcon
            newResources={onNewAssets}
            onAssetProcessing={onAssetProcessing}
            sectionKey={section.section_key}
            uniqDomElement={section.section_key}
            uploadType="asset"
          />
          {canvaEnabled && defaultAssetType === "GenericFile"
            && (
              <CanvaButton
                apiKey="rL0oMltMP2ORjYdrCQ-jwz3A"
                sectionKey={section.section_key}
                onNewAssets={onNewAssets}
                onAssetProcessing={onAssetProcessing}
              />
            )
          }
        </div>
      );
    } else {
      uploader = (
        <div className="base-card generic">
          <button
            type="button"
            className="generic-uploader"
            onClick={newAssetUI}
          >
            <span className="bff-plus" />
            <p>{t('Add an Asset')}</p>
          </button>
        </div>
      );
    }

    let insertCaretDirection = '';
    let insertCaretId;
    if (insertCaret) {
      insertCaretDirection = insertCaret.direction;
      insertCaretId = insertCaret.id;
    }

    const customFieldsKeys = processCustomFields(assets);

    return (
      <SectionsContext.Consumer>
        {({
          editable,
          listView,
          parentBrandfolders,
          showSections,
          sortOption,
        }) => {
          const disableSort = BFG.resource?.type !== 'brandfolder'
            || !BFG.brandfolderSettings?.allow_positioning
            || (sortOption !== "position ASC")
            || !editable
            || !userViewOptions.showSections;
          const disableUpload = !editable || BF_Manifest || (!showSections && activeSection === 'all');

          return (
            <div
              className={`card-list ${defaultAssetType === "Text" ? "text-list" : ""} ${disableSort ? "disable-sort" : ""}`}
            >
              {disableUpload ? null : uploader }
              {listView && assets.length > 0 && (
                <div className="list-view-headers">
                  {userViewOptions.showCustomFields ? (
                    <>
                      <h4 className="header-copy asset-name">{t('Name')}</h4>
                      {customFieldsKeys.map((key) => (
                        <h4 className="header-copy custom-field-key" key={key}>{key}</h4>
                      ))}
                      {/* placeholder for the 50px action menu width */}
                      <span className="list-view-headers-placeholder" />
                    </>
                  ) : (
                    <>
                      <h4 className="header-copy asset-name">{t('Name')}</h4>
                      <h4 className="header-copy asset-tags">{t('Tags')}</h4>
                      {/* placeholder for the 50px action menu width */}
                      <span className="list-view-headers-placeholder" />
                    </>
                  )}
                </div>
              )}
              {assets.map((asset) => (
                <Asset
                  asset={asset}
                  customFieldsKeys={customFieldsKeys}
                  enableContactSheets={enableContactSheets}
                  key={asset.id}
                  insertCaretDirection={asset.id === insertCaretId ? insertCaretDirection : ''}
                  lazyLoadCards={lazyLoadCards}
                  onMove={(item, hoverId, pointerOffset) => this.onAssetHover(item, hoverId, pointerOffset, listView)}
                  onDragComplete={this.onDragComplete}
                  onDrop={this.onDrop}
                  parentBrandfolders={parentBrandfolders}
                  ref={(ref) => { this.assetRefs[asset.id] = ref; }}
                  reorderable={!disableSort}
                  section={section}
                  selected={selectedAssetKeys.has(asset.id)}
                  selectedAssetKeys={selectedAssetKeys}
                  shiftSelect={shiftSelect}
                  toggleSelected={this.toggleSelected}
                  userViewOptions={userViewOptions}
                />
              ))}
            </div>
          );
        }}
      </SectionsContext.Consumer>
    );
  }

  renderShroud({ editable }) {
    const { assets } = this.props;
    const assetCount = editable ? assets.length + 1 : assets.length;
    return (
      <SectionShroud
        assetCount={assetCount}
        onShown={() => {
          this.setState({ isRendered: true });
        }}
      />
    );
  }

  render() {
    const {
      assets,
      section,
      totalPages,
      totalAssets,
      allAssetsSelected,
      lazyLoadCards,
      userViewOptions,
    } = this.props;

    const {
      isRendered,
    } = this.state;

    return (
      <SectionsContext.Consumer>
        {({ activeLabelKey, editable, listView }) => {
          if (
            (BF_Manifest && assets.length <= 0)
            || (assets.length <= 0 && !editable)
          ) {
            return null;
          }

          const visibleChecked = this.sectionSelected() && !allAssetsSelected;

          return (
            <article
              className={`section-container
                ${this.isNotActiveSection() ? "hidden" : ""}
                ${listView ? "list-view" : "card-view"}`}
              id={section.section_key}
            >
              <div className="section-header no-print">
                <div className="section-title-count">
                  <div className="section-title-checkbox-container">
                    <div className="section-title-checkbox-wrapper no-print">
                      <Checkbox
                        checked={totalPages > 1 ? allAssetsSelected : visibleChecked}
                        size="md"
                        toggle={() => this.toggleSelectedAssets('page', { activeLabelKey })}
                        partiallySelected={visibleChecked && totalPages > 1 ? 'partially-selected' : null}
                      />
                      {totalPages > 1 && userViewOptions.showSections && (
                        <ul className="section-title-selection-options">
                          <li>
                            <Checkbox
                              checked={visibleChecked}
                              size="md"
                              toggle={() => this.toggleSelectedAssets('page', { activeLabelKey })}
                              labelHtml={(
                                <>
                                  {t('Current page')}
                                  <span className="section-title-selection-options__asset_count">
                                    {`${assets.length} ${t('assets')}`}
                                  </span>
                                </>
                              )}
                            />
                          </li>
                          <li>
                            <Checkbox
                              checked={allAssetsSelected}
                              size="md"
                              toggle={() => this.toggleSelectedAssets('all', { activeLabelKey })}
                              labelHtml={(
                                <>
                                  {t('All pages')}
                                  <span className="section-title-selection-options__asset_count">
                                    {`${totalAssets} ${t('assets')}`}
                                  </span>
                                </>
                              )}
                            />
                          </li>
                        </ul>
                      )}
                    </div>
                    <p className="section-title">{section.name}</p>
                    {this.renderSyncInDesign()}
                  </div>
                  {this.renderPageCount()}
                </div>
                {totalPages > 1 && isRendered ? (
                  <ReactPaginate
                    activeClassName="active"
                    breakClassName="break-me"
                    breakLabel="..."
                    containerClassName="pagination"
                    marginPagesDisplayed={1}
                    nextLabel={this.nextLabel()}
                    onPageChange={this.goToPage}
                    pageCount={totalPages}
                    previousLabel={this.previousLabel()}
                    pageRangeDisplayed={3}
                    subContainerClassName="page-box"
                  />
                ) : (
                  ""
                )}
              </div>
              {isRendered || !lazyLoadCards ? this.renderAssets(activeLabelKey) : this.renderShroud({ editable })}
            </article>
          );
        }}
      </SectionsContext.Consumer>
    );
  }
}

Section.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape({})),
  activeSection: PropTypes.string,
  authenticityToken: PropTypes.string,
  section: PropTypes.shape({
    id: PropTypes.number,
    section_key: PropTypes.string,
  }).isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
  toggleSelected: PropTypes.func.isRequired,
  shiftSelect: PropTypes.func.isRequired,
  printuiSectionId: PropTypes.number,
  editable: PropTypes.bool,
  onNewAssets: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  totalAssets: PropTypes.number,
  loading: PropTypes.bool,
  isRendered: PropTypes.bool,
  allAssetsSelected: PropTypes.bool,
  searchQuery: PropTypes.string,
  onAssetProcessing: PropTypes.func,
  lazyLoadCards: PropTypes.bool,
  enableContactSheets: PropTypes.bool,
  userViewOptions: PropTypes.shape({
    assetsPerPage: PropTypes.number,
    showAsGrid: PropTypes.bool,
    showCustomFields: PropTypes.bool,
    showEmptySections: PropTypes.bool,
    sortOrder: PropTypes.string,
    showSections: PropTypes.bool,
  }),
};

Section.defaultProps = {
  assets: [],
  activeSection: "all",
  authenticityToken: null,
  printuiSectionId: null,
  editable: false,
  totalPages: 1,
  currentPage: 1,
  totalAssets: 0,
  loading: true,
  isRendered: true,
  allAssetsSelected: false,
  searchQuery: null,
  onAssetProcessing: () => {},
  lazyLoadCards: true,
  enableContactSheets: false,
  userViewOptions: {},
};

export default Section;
